import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, svg, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref, type Ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import '@schmancy/primitives/surface'
import '@schmancy/primitives/typography'
import { FILL } from './chart.mixin'
import type { ChartColorToken, ChartPoint } from './types'

/** One ordered step of the funnel. `value` is absolute; every share this
 *  primitive renders is derived against the FIRST stage's value, so a
 *  percentage is never passed in and never disagrees with the shape. */
export interface FunnelStage {
	/** Stable identity for the keyed render. */
	id: string
	label: string
	value: number
}

/** The cohort that left between two stages — what `dropName` is handed when a
 *  consumer names the gap itself. */
export interface FunnelDrop {
	/** Absolute figure: `from.value - to.value`, floored at zero. */
	value: number
	from: FunnelStage
	to: FunnelStage
}

export type SchmancyFunnelActivateDetail =
	| {
			kind: 'stage'
			id: string
			stage: FunnelStage
			index: number
			originalEvent: Event
	  }
	| {
			kind: 'drop'
			id: string
			drop: FunnelDrop
			index: number
			originalEvent: Event
	  }

export type SchmancyFunnelActivateEvent = CustomEvent<SchmancyFunnelActivateDetail>

/** How the void between two bands is toned. `neutral` is the default because
 *  a buyer who left did nothing wrong; `error`/`warning` are for a drop the
 *  system itself refused. Deliberately disjoint from the series color, so the
 *  cohort that continues and the cohort that leaves never share a hue. */
export type FunnelLossTone = 'neutral' | 'warning' | 'error'

const LOSS_FILL: Record<FunnelLossTone, string> = {
	// `surface-on-variant`, not `outline-variant`: the void has to read against
	// the page ground in BOTH schemes, and outline-variant is a dark grey on a
	// dark ground — at any opacity that keeps it subordinate to the wall it is
	// indistinguishable from the ground it sits on, so the drop stops being
	// visible exactly where the shape is supposed to carry it. The on-variant
	// ink is the one neutral the theme guarantees legible over the surface
	// either way round.
	neutral: 'fill-surface-on-variant',
	warning: 'fill-warning-default',
	error: 'fill-error-default',
}

const LOSS_TEXT: Record<FunnelLossTone, string> = {
	neutral: 'text-surface-on-variant',
	warning: 'text-warning-default',
	error: 'text-error-default',
}

/** How hard the void is painted against the band beside it. The wedges are the
 *  cohort that left and the band is the cohort that stayed, so they are the one
 *  pair in the shape that must not read as equals.
 *
 *  This is also the whole boundary between them: the wedges ABUT the trapezoid
 *  with no spacer, where `<schmancy-segmented-bar>` separates two adjacent
 *  members with a constant 2 px. The two charts answer that question
 *  differently on purpose. A segmented bar is a row of members, and a gap is
 *  what tells one member from the next; a funnel is ONE silhouette, and a gap
 *  cut into it would read as a break in the cohort rather than as a boundary,
 *  which is exactly the seam every other rule in this file exists to prevent.
 *  The wedge is told from the band by tone and opacity instead — both measured
 *  against the ground in `funnel.test.ts`. */
const LOSS_OPACITY = '0.3'

/** Design-space width of every strip this component draws: x units are
 *  percent of the rendered width, y units are real pixels (the strip's own
 *  height is its viewBox height, so `preserveAspectRatio="none"` stretches x
 *  alone). Nothing round is drawn here, so that stretch costs no geometry. */
const FUNNEL_W = 100

/** Floor on a NON-ZERO band's own width, in REAL PIXELS rather than in percent
 *  of the plot. The floor's job is visibility, and a percent floor promises
 *  something different at every width — 4 % is 8 px against a 204 px plot and
 *  would be 22 px against a 560 px one, over-flooring by nearly three and lying
 *  about the data far more than it needs to. 8 px against a 32–44 px band
 *  height reads as a narrow post: visibly a band, visibly tiny, which is the
 *  honest rendering of "almost nobody". A stage that kept nobody is not
 *  floored — its own name says "0, 0 %", and a band beside that figure is the
 *  shape contradicting the text. */
const MIN_BAND_PX = 8

/** What the floor falls back to before the first `ResizeObserver` reading
 *  lands, in the same percent units the bands are drawn in: a first frame has
 *  no measured plot to state a pixel floor against. */
const UNMEASURED_MIN_BAND_PCT = 4

/** Floor on `bandHeight`: one line of `label/sm` plus one figure line. Below
 *  it the name's own flex allowance can be squeezed to zero and the stage
 *  renders nameless, which is a worse failure than a dense band. */
const MIN_BAND_HEIGHT_PX = 32

/** The widest the plot column may be, as a multiple of the silhouette's own
 *  painted height. A shape free to take the whole host stops reading as a
 *  vessel: at 4.8 : 1 the first band is a wall and the eye never assembles a
 *  funnel, where 1.35 : 1 reads as one unmistakably. This is the widest ratio
 *  confirmed to still read, and it costs no measure→layout→measure pass
 *  because that height is width-independent — band height is a prop and every
 *  run height is a function of the data. */
const PLOT_WIDEST_RATIO = 1.6

/** The clamp around that ratio: the floor keeps a two-stage funnel from
 *  collapsing to a sliver, the ceiling keeps a ten-stage one from sprawling
 *  back toward a wall. */
const PLOT_MIN_PX = 240
const PLOT_MAX_PX = 560

/** Floor on a run that PRINTS a figure. The gutter cell is one pooled flex
 *  budget — band + run — and the figure lines in it do not shrink, so a run too
 *  short to hold the line it now carries charges the shortfall to the NAME,
 *  which is clipped for a reason it had no part in. One `label/xs` line is the
 *  least a figured run can be worth. A gap that lost nobody prints nothing and
 *  keeps its bare `dropHeight`. */
const MIN_FIGURED_RUN_PX = 14

/** What the breakdown needs beside the shape before it earns its own column:
 *  288px (`--container-2xs`) is the narrowest a segmented bar with a key still
 *  reads, and 49px is the rule and its inset — the `border-l` + `ml-6` + `pl-6`
 *  that column carries there. */
const BREAKDOWN_COLUMN_PX = 288 + 49

/** What the grid's own `11rem` gutter ceiling and `gap-x-3` resolve to at the
 *  default root font size. ONLY the beside/below threshold reads them — every
 *  length this component paints is a `calc()` in those same rem units — so a
 *  root font size past 16px moves the threshold a little and leaves the
 *  geometry exact. */
const GUTTER_COLUMN_PX = 176 + 12

interface FunnelBand {
	stage: FunnelStage
	/** `value / stages[0].value`, clamped to [0, 1]. */
	share: number
	/** The band's painted width in percent — `share` past the pixel floor. */
	widthPct: number
	/** What the formatter hooks are handed for this band, in the shape every
	 *  other chart in this folder hands them. */
	point: ChartPoint<FunnelStage>
}

/** One run between two bands, resolved before the row is drawn because three
 *  cells read it: the connector paints it, the gutter beside it is bounded by
 *  band + run, and the gap's own figure hangs off the bottom of that bound. */
interface FunnelRun {
	drop: number
	/** `drop / stages[0].value` — the same denominator every band uses, which
	 *  is what makes each adjacent triple in the gutter sum. */
	share: number
	/** The run's own height in whole pixels. Both channels of one loss live
	 *  here — the wedges carry it across, this carries it down. */
	height: number
	/** The connector's accessible name, or `undefined` where there is nothing
	 *  to name — which is also where there is nothing to figure. */
	name?: string
	point: ChartPoint<FunnelStage>
}

/**
 * A proportional conversion funnel: ordered stages, each a centred horizontal
 * band whose width is its share of the first stage, so the taper reads as one
 * narrowing shape rather than as a column of numbers.
 *
 * THE DROP IS THE SHAPE. Between band N and band N+1 the primitive paints the
 * connector: the central trapezoid is the cohort that continued, and the two
 * wedges flanking it — the void the taper carves out — are the cohort that
 * left, at the same proportional scale as the bands themselves. The connector's
 * HEIGHT carries that same loss a second way, so a shallow step reads shallow
 * and a collapse reads like one at a glance rather than by comparing widths.
 *
 * THE PLOT IS CAPPED AGAINST ITS OWN HEIGHT — `clamp(240px, 1.6 × H, 560px)`,
 * where H is the silhouette's painted height. The width of a funnel is not a
 * property of the box it was dropped into; a shape allowed to stretch to any
 * host width while its height stays a function of the data alone stops being a
 * vessel. The cap is what makes this one geometry at every width rather than a
 * responsive problem to manage, and the room it gives back goes to the
 * `loss-breakdown` slot, which moves beside the shape once the component's own
 * box can hold both.
 *
 * ALL TEXT LIVES IN THE LEADING GUTTER; the plot column is pure geometry. What
 * lives between two bands is the taper, and the taper is the drop, so anything
 * else rendered there breaks the one continuous silhouette the shape is. Per
 * stage the gutter carries the name, then the stage's own figure and its share,
 * then — hanging off the bottom of the run below it, right-aligned against the
 * plot's own left edge — that gap's drop and the drop's share. Names form the
 * left rail and losses an indented right one, so the loss sits between the two
 * stages it separates, at the taper's own y, adjacent to the notch it names at
 * every width. Every figure is always painted, never revealed, because a figure
 * that appears on hover is a figure a touch reader never has.
 *
 * EVERY SHARE IS MEASURED AGAINST THE FIRST STAGE — the same denominator the
 * bands' widths and the runs' heights already use. That is what makes each
 * adjacent triple in the gutter sum (72 + 28 = 100, 7 + 21 = 28), so a reader
 * can check the chart against itself without leaving it, and it is what keeps
 * a printed percentage from disagreeing with the wedge painted beside it. The
 * first stage prints no share: it IS the denominator. Each band and each
 * connector is its own `role="img"` with a name carrying its figures, so the
 * whole shape is reachable without reading the pixels; the gutter's own text is
 * hidden from assistive technology as the duplicate it is.
 *
 * A per-stage `figure-<stage.id>` slot sits under each gutter name, holding the
 * stage's own value by default. A consumer fills it when that figure has to be
 * pressable — a surface where a number opens its sources cannot reach one this
 * shadow root prints; its height is `bandHeight`, so a consumer that slots an
 * interactive figure buys its own 44 px target with `bandHeight="44"`. The
 * gutter is bounded to the band plus the run beside it, so nothing slotted
 * there can push the next band down and seam the silhouette; a name that
 * outgrows that bound fades out at the bound rather than being cut off
 * mid-glyph with nothing said about it.
 */
@schmancyElement('schmancy-funnel')
export class SchmancyFunnel extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}

			[part='band'][role='button'],
			[part='drop'][role='button'] {
				cursor: pointer;
				outline: none;
			}

			[part='band'][role='button']:hover rect,
			[part='drop'][role='button']:hover polygon {
				filter: brightness(1.08);
			}

			[part='band'][role='button']:focus-visible,
			[part='drop'][role='button']:focus-visible {
				outline: 2px solid var(--schmancy-sys-color-secondary-default, currentColor);
				outline-offset: 2px;
			}

			/* The gutter's bound is the silhouette's: a name taller than band + run
			   would push the next band down and open a seam. Clipping it is the
			   enforcement; this fade is the SIGNAL that it happened, and it is
			   conditional on a measurement (scrollHeight past clientHeight) that
			   no class can express — a utility cannot be written for "only when
			   this box overflowed". It rides the NAME rather than the cell so the
			   flex column absorbs every overflow into the one element that may be
			   clipped: the figures and the shares are never dropped, which is what
			   the always-painted rule requires. Nothing on this element carries a
			   mask-* class, so no utility is being shadowed here. The full name
			   stays readable in the band's own accessible name either way. */
			[part='stage-label'][data-clipped] {
				mask-image: linear-gradient(to bottom, #000 calc(100% - 0.75rem), transparent 100%);
			}
		`,
	]

	/** Ordered, first stage first — the first stage's value is the denominator
	 *  of every share and the full width of the shape. */
	@property({ type: Array }) stages: FunnelStage[] = []

	/** Height of one band, in px. Floored at 32 — a band shorter than one name
	 *  line plus one figure line renders the stage nameless. A consumer slotting
	 *  a pressable figure into `figure-<id>` buys its 44 px touch target here. */
	@property({ type: Number }) bandHeight = 40

	/** The connector's height when nothing was lost, in px — the floor that
	 *  keeps a zero-drop gap a straight-sided section of the silhouette rather
	 *  than a collapsed seam. */
	@property({ type: Number }) dropHeight = 20

	/** How much taller the connector grows between losing nobody and losing the
	 *  whole top cohort, in px. This is the second channel the loss rides: the
	 *  wedges carry it across, this carries it down. */
	@property({ type: Number }) dropHeightSpan = 86

	/** The series color. One hue for every band: the taper already carries the
	 *  ordinal progression and the magnitude, so a per-stage ramp would
	 *  double-encode what the width shows. */
	@property({ type: String }) color: ChartColorToken = 'primary'

	/** Makes the painted stage bands and named drop wedges the interaction
	 * surface. Slotted figures remain their own drill controls; this opt-in
	 * gives the geometry a direct, duplicate-safe action path. */
	@property({ type: Boolean }) interactive = false

	/** The tone of the void between bands — see `FunnelLossTone`. */
	@property({ type: String }) loss: FunnelLossTone = 'neutral'

	/** Names the wedge once, beneath the shape — "dropped out", "abgesprungen".
	 *  One legend for every gap, because the wedge means the same thing at each
	 *  of them and a per-gap word is the prose this shape replaces. Two fills
	 *  carrying two cohorts need a key, so this word is what the legend renders
	 *  and the default is deliberately visible: a consumer who has not
	 *  translated it yet reads it on the page rather than only in a screen
	 *  reader. Blanking it stands the key down and moves the obligation to name
	 *  the wedge onto the consumer's own surface. */
	@property({ type: String }) lossLabel = 'dropped out'

	/** Names one gap for assistive technology. Every word of the default name
	 *  is already the consumer's — the two stage labels, the formatted figure,
	 *  `lossLabel` and the formatted share — and the primitive contributes
	 *  punctuation only, because a joiner it invented would be ITS language
	 *  inside a name the consumer otherwise translates in full. Supply this
	 *  where the gap needs a sentence with that language's own grammar. */
	@property({ attribute: false }) dropName?: (drop: FunnelDrop) => string

	/** Formats every absolute figure — a stage's value and a gap's drop alike.
	 *  Two arguments, as everywhere else in this folder: the figure, and the
	 *  point it belongs to. A gap's point carries the drop as its `value` and
	 *  the stage the cohort left FROM as its `data`. */
	@property({ attribute: false }) valueFormatter?: (v: number, p: ChartPoint<FunnelStage>) => string

	@property({ attribute: false }) shareFormatter?: (share: number) => string

	/** Real rendered width of the plot column, measured on its own box. `0`
	 *  until the first `ResizeObserver` reading lands, which the band floor
	 *  reads as "not measured yet" and falls back to a percent for. */
	@state() private plotWidth = 0

	/** Real rendered width of the host — the box the whole assembly is laid out
	 *  in, and the axis the breakdown's beside/below composition is decided on.
	 *  It is the HOST rather than the plot because the host's own width does not
	 *  move when that composition changes, so the branch cannot oscillate. */
	@state() private hostWidth = 0

	private plotRef: Ref<HTMLDivElement> = createRef()
	private hostRef: Ref<HTMLDivElement> = createRef()
	private sizeObserver: ResizeObserver | null = null
	/** Whether anything is slotted into `loss-breakdown`; an empty slot must
	 *  not pay for the rule and the gap a filled one earns, and must not take
	 *  the second column that would strand the shape against the left edge. */
	@state() private hasLossBreakdown = false

	connectedCallback() {
		super.connectedCallback()
		// Guarded because schmancy ships as a library whose modules can be
		// imported without a DOM present, where `ResizeObserver` may not exist.
		if (typeof ResizeObserver !== 'undefined') {
			this.sizeObserver = new ResizeObserver(entries => {
				for (const entry of entries) {
					if (entry.target === this.plotRef.value) this.plotWidth = entry.contentRect.width
					else this.hostWidth = entry.contentRect.width
				}
			})
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.sizeObserver?.disconnect()
		this.sizeObserver = null
	}

	protected updated(changed: PropertyValues): void {
		super.updated(changed)
		if (this.sizeObserver) {
			if (this.plotRef.value) this.sizeObserver.observe(this.plotRef.value)
			if (this.hostRef.value) this.sizeObserver.observe(this.hostRef.value)
		}
		// A plain attribute rather than reactive state: the fade is pure CSS off
		// it, so writing it here costs no second render and cannot loop.
		for (const name of this.renderRoot.querySelectorAll<HTMLElement>('[part="stage-label"]')) {
			const clipped = name.scrollHeight > name.clientHeight + 1
			// Only the rendered cell knows its own budget — neither floor in this
			// file is sufficient, because a consumer's own slotted figure can take
			// any share of it — so the clip is said out loud where it is observed
			// rather than left as a fade nobody can price.
			if (import.meta.env.DEV && clipped && !name.hasAttribute('data-clipped'))
				console.warn(
					`<schmancy-funnel>: the name "${name.textContent?.trim()}" is clipped. Its gutter cell is bandHeight plus the run below it (${name.parentElement?.clientHeight}px), the figure and drop lines in it do not shrink, and the name is left ${name.clientHeight}px of the ${name.scrollHeight}px it wants. Raise bandHeight.`,
				)
			name.toggleAttribute('data-clipped', clipped)
		}
	}

	private formatValue(v: number, p: ChartPoint<FunnelStage>): string {
		return this.valueFormatter?.(v, p) ?? String(v)
	}

	private formatShare(share: number): string {
		return this.shareFormatter?.(share) ?? `${Math.round(share * 100)}%`
	}

	private activateStage(band: FunnelBand, index: number, originalEvent: Event): void {
		this.dispatchEvent(
			new CustomEvent<SchmancyFunnelActivateDetail>('funnel-activate', {
				detail: { kind: 'stage', id: band.stage.id, stage: band.stage, index, originalEvent },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private activateDrop(run: FunnelRun, index: number, originalEvent: Event): void {
		const from = this.stages[index]
		const to = this.stages[index + 1]
		if (!from || !to) return
		this.dispatchEvent(
			new CustomEvent<SchmancyFunnelActivateDetail>('funnel-activate', {
				detail: {
					kind: 'drop',
					id: String(run.point.id ?? `${from.id}→${to.id}`),
					drop: { value: run.drop, from, to },
					index,
					originalEvent,
				},
				bubbles: true,
				composed: true,
			}),
		)
	}

	private activateFromKeyboard(originalEvent: KeyboardEvent, activate: (event: KeyboardEvent) => void): void {
		if (originalEvent.key !== 'Enter' && originalEvent.key !== ' ') return
		originalEvent.preventDefault()
		activate(originalEvent)
	}

	render() {
		const top = this.stages[0]?.value ?? 0
		// A funnel whose first stage never happened has no denominator, so it
		// has no proportions to draw. It keeps the ladder all the same — one
		// full-width neutral track per stage, separated by the connector's own
		// floor, gutter names and all — since an empty range is an answer, not a
		// missing surface.
		const empty = top <= 0
		const bandHeight = Math.max(MIN_BAND_HEIGHT_PX, Math.round(this.bandHeight))
		if (import.meta.env.DEV) {
			const ids = new Set(this.stages.map(stage => stage.id))
			if (ids.size !== this.stages.length)
				console.warn(
					'<schmancy-funnel>: two stages share an id. The keyed render and the per-stage `figure-<id>` slot both resolve by that id, so only the first of each pair is reachable.',
					this.stages,
				)
		}
		const minPct = this.plotWidth > 0 ? (MIN_BAND_PX / this.plotWidth) * FUNNEL_W : UNMEASURED_MIN_BAND_PCT
		const bands: FunnelBand[] = this.stages.map(stage => {
			const share = empty ? 0 : Math.min(1, Math.max(0, stage.value / top))
			return {
				stage,
				share,
				widthPct: empty ? FUNNEL_W : share > 0 ? Math.max(minPct, share * FUNNEL_W) : 0,
				point: { value: stage.value, id: stage.id, label: stage.label, data: stage },
			}
		})
		const runs: FunnelRun[] = bands.map((band, index) => {
			const next = bands[index + 1]
			const drop = next && !empty ? Math.max(0, band.stage.value - next.stage.value) : 0
			const share = empty ? 0 : drop / top
			const height = next ? Math.round(empty ? this.dropHeight : this.dropHeight + share * this.dropHeightSpan) : 0
			const point: ChartPoint<FunnelStage> = {
				value: drop,
				id: `${band.stage.id}→${next?.stage.id ?? ''}`,
				label: band.stage.label,
				data: band.stage,
			}
			if (!next || empty || drop <= 0) return { drop, share, height, point }
			return {
				drop,
				share,
				height: Math.max(MIN_FIGURED_RUN_PX, height),
				name:
					this.dropName?.({ value: drop, from: band.stage, to: next.stage }) ??
					`${band.stage.label}, ${next.stage.label}: ${[this.formatValue(drop, point), this.lossLabel]
						.filter(Boolean)
						.join(' ')}, ${this.formatShare(share)}`,
				point,
			}
		})
		const widest = runs.reduce((worst, run) => (run.drop > worst.drop ? run : worst), runs[0])
		const silhouette = bands.length * bandHeight + runs.reduce((total, run) => total + run.height, 0)
		const plotCap = Math.min(PLOT_MAX_PX, Math.max(PLOT_MIN_PX, PLOT_WIDEST_RATIO * silhouette))
		// The breakdown earns a column beside the shape when the host can hold
		// BOTH — never at a width the shape's own cap is unrelated to, which is
		// what a fixed breakpoint would be: at the cap's ceiling the assembly is
		// 748px, and a breakpoint that fires before it leaves the key truncated.
		const beside = this.hasLossBreakdown && this.hostWidth >= GUTTER_COLUMN_PX + plotCap + BREAKDOWN_COLUMN_PX

		return html`
			<!-- The host's own width, read off a strip with no height of its own: the
			     host churns in the block axis on every render, and a reading taken
			     from the host itself re-enters the very resize notification the write
			     came from, which the browser ends by dropping one. This strip sits
			     outside the composition below, so its width is the host's whatever
			     that composition resolves to. -->
			<div ${ref(this.hostRef)} class="h-0 w-full"></div>

			<!-- Every responsive decision here is taken against the component's own
			     box, never the viewport: the same funnel sits at 1112px of host
			     inside an analytics module and could sit at 300px inside a
			     two-column panel on the identical viewport, and a viewport
			     breakpoint cannot tell those apart. -->
			<div
				class="${this.classMap({
					flex: true,
					'flex-col': !beside,
					'flex-row': beside,
				})}"
				style=${this.styleMap({ '--funnel-plot': `${plotCap}px` })}
			>
				<!-- The plot's cap rides the TRACK rather than a max-width on each
				     cell, so the leftover it leaves is the grid's own and
				     justify-center hands it back symmetrically: the silhouette
				     centres in whatever box the host gives it with no dead track
				     trailing the plot. Every length here is a calc() in the grid's
				     own rem units, so nothing in this layout can disagree with
				     itself at a root font size other than 16px. -->
				<div
					class="${this.classMap({
						'grid items-start justify-center gap-x-3': true,
						'grid-cols-[minmax(0,min(34%,11rem))_minmax(0,var(--funnel-plot))]': true,
						// Exclusive, never both: two width utilities on one element are
						// settled by the sheet's own source order, not by which one this
						// branch meant.
						'w-full': !beside,
						'w-[calc(11rem+0.75rem+var(--funnel-plot))] shrink-0 self-start': beside,
					})}"
				>
					${repeat(
						bands,
						band => band.stage.id,
						(band, index) => {
							const next = bands[index + 1]
							const run = runs[index]
							const halfOwn = band.widthPct / 2

							return html`
								<div
									part="stage-gutter"
									class="flex min-w-0 flex-col overflow-hidden"
									style=${this.styleMap({
										minHeight: `${bandHeight}px`,
										// The run below this band belongs to the taper, so the gutter
										// is not allowed to lengthen it: a name (or a slotted figure)
										// taller than band + run would push the next band down and open
										// a seam in the one continuous silhouette. A DEFINITE height
										// rather than a bound on one, because `mt-auto` cannot pin the
										// gap's figure to the bottom of a box sized by its own content —
										// and with `overflow-hidden` it is the stronger guarantee, since
										// the cell can no longer reach the bound at all. The last stage
										// has no run after it, so nothing there can break and its name is
										// free to wrap as far as it needs.
										height: next ? `${bandHeight + run.height}px` : undefined,
									})}
								>
									<schmancy-typography
										part="stage-label"
										type="label"
										token="sm"
										weight="bold"
										class="min-h-0 overflow-hidden hyphens-auto break-words"
										aria-hidden="true"
										>${band.stage.label}</schmancy-typography
									>
									<div class="flex shrink-0 items-baseline gap-1">
										<slot name=${`figure-${band.stage.id}`}>
											<schmancy-typography
												part="stage-value"
												type="label"
												token="xs"
												class="tabular-nums text-surface-on-variant"
												aria-hidden="true"
												>${this.formatValue(band.stage.value, band.point)}</schmancy-typography
											>
										</slot>
										${
											// The share sits OUTSIDE the slot, so a consumer who fills the
											// slot with a pressable figure still gets it. The first stage
											// prints none: it is the denominator, and `100 %` beside a band
											// that is the whole plot restates the picture.
											when(
												index > 0,
												() =>
													html`<schmancy-typography
															type="label"
															token="xs"
															class="text-surface-on-variant"
															aria-hidden="true"
															>·</schmancy-typography
														>
														<schmancy-typography
															part="stage-share"
															type="label"
															token="xs"
															class="tabular-nums text-surface-on-variant"
															aria-hidden="true"
															>${this.formatShare(band.share)}</schmancy-typography
														>`,
											)
										}
									</div>
									${when(
										run.name,
										() =>
											html`<div
												class="${this.classMap({
													'mt-auto flex shrink-0 items-baseline justify-end gap-1': true,
													[LOSS_TEXT[this.loss]]: true,
												})}"
											>
												<schmancy-typography
													part="drop-figure"
													type="label"
													token="xs"
													class="tabular-nums"
													aria-hidden="true"
													>−${this.formatValue(run.drop, run.point)}</schmancy-typography
												>
												<schmancy-typography type="label" token="xs" aria-hidden="true">·</schmancy-typography>
												<schmancy-typography
													part="drop-share"
													type="label"
													token="xs"
													class="tabular-nums"
													aria-hidden="true"
													>${this.formatShare(run.share)}</schmancy-typography
												>
											</div>`,
									)}
								</div>

								<div class="w-full">
									<svg
										part="band"
										viewBox="0 0 ${FUNNEL_W} ${bandHeight}"
										preserveAspectRatio="none"
										class=${this.classMap({
												'block w-full': true,
												'cursor-pointer': this.interactive,
											})}
										style=${this.styleMap({ height: `${bandHeight}px` })}
										role=${this.interactive ? 'button' : 'img'}
										tabindex=${ifDefined(this.interactive ? '0' : undefined)}
										aria-label=${`${band.stage.label}: ${this.formatValue(band.stage.value, band.point)}, ${this.formatShare(band.share)}`}
										@click=${(event: MouseEvent) => {
												if (this.interactive) this.activateStage(band, index, event)
											}}
										@keydown=${(event: KeyboardEvent) => {
												if (this.interactive)
													this.activateFromKeyboard(event, keyEvent => this.activateStage(band, index, keyEvent))
											}}
									>
										${svg`
																							<rect
																								part="band-mark"
														x=${(FUNNEL_W - band.widthPct) / 2}
														y="0"
														width=${band.widthPct}
														height=${bandHeight}
														class=${empty ? 'fill-surface-containerHighest' : FILL[this.color]}
													></rect>
												`}
									</svg>

									${when(next, next => {
										const halfNext = next.widthPct / 2
										// ONE representation of the run between two bands, whatever the
										// data says: the connector. What it PAINTS follows the data —
										// with no denominator there is no cohort to draw, so it stays an
										// empty section holding the ladder apart (painting it would make
										// four stages with no data read as one slab), and with nothing to
										// name it is not announced.
										return html`
											<div
												part="drop"
												class=${this.classMap({
													'relative w-full': true,
													'cursor-pointer': this.interactive && !!run.name,
												})}
												style=${this.styleMap({ height: `${run.height}px` })}
												role=${ifDefined(run.name ? (this.interactive ? 'button' : 'img') : undefined)}
												tabindex=${ifDefined(this.interactive && run.name ? '0' : undefined)}
												aria-label=${ifDefined(run.name)}
												@click=${(event: MouseEvent) => {
													if (this.interactive && run.name) this.activateDrop(run, index, event)
												}}
												@keydown=${(event: KeyboardEvent) => {
													if (this.interactive && run.name)
														this.activateFromKeyboard(event, keyEvent => this.activateDrop(run, index, keyEvent))
												}}
											>
												${when(
													!empty,
													() =>
														html`<svg
															viewBox="0 0 ${FUNNEL_W} ${run.height}"
															preserveAspectRatio="none"
															class="absolute inset-0 h-full w-full"
															aria-hidden="true"
														>
															${svg`
																					<polygon
																						part="drop-mark"
																		points="${50 - halfOwn},0 ${50 + halfOwn},0 ${50 + halfNext},${run.height} ${50 - halfNext},${run.height}"
																		class=${FILL[this.color]}
																	></polygon>
																`}
															${when(
																run.drop > 0 && halfOwn > halfNext,
																() => svg`
																		<polygon
																			points="${50 - halfOwn},0 ${50 - halfNext},${run.height} ${50 - halfOwn},${run.height}"
																			class=${LOSS_FILL[this.loss]}
																			fill-opacity=${LOSS_OPACITY}
																		></polygon>
																		<polygon
																			points="${50 + halfOwn},0 ${50 + halfOwn},${run.height} ${50 + halfNext},${run.height}"
																			class=${LOSS_FILL[this.loss]}
																			fill-opacity=${LOSS_OPACITY}
																		></polygon>
																	`,
															)}
														</svg>`,
												)}
											</div>
										`
									})}
								</div>
							`
						},
					)}

					<!-- The plot column's own width, which is what the band floor reads:
						     the pixels it floors against are a fraction of this box, never of
						     the host, and the gutter beside it is not part of either. -->
					<div ${ref(this.plotRef)} class="col-start-2 h-0"></div>

					${when(
						!!this.lossLabel && !empty && (widest?.drop ?? 0) > 0,
						() => html`
							<!-- Inside the grid, so the key sits under the gutter it belongs
								     to rather than stretching away from the shape it names. -->
							<div part="loss-legend" class="col-span-2 flex items-center gap-1.5 pt-3">
								<svg viewBox="0 0 12 10" preserveAspectRatio="none" class="h-2.5 w-3 shrink-0" aria-hidden="true">
									${svg`<polygon points="0,0 12,10 0,10" class=${LOSS_FILL[this.loss]} fill-opacity=${LOSS_OPACITY}></polygon>`}
								</svg>
								<schmancy-typography type="label" token="xs" class="${this.classMap({ [LOSS_TEXT[this.loss]]: true })}"
									>${this.lossLabel}</schmancy-typography
								>
							</div>
						`,
					)}
				</div>

				<!-- Below the shape the rule spans the SILHOUETTE, not the host: the
				     same calc the grid's own tracks resolve to, so a breakdown under a
				     capped funnel in a wide box does not overhang the shape it keys.
				     Beside it, the column stretches to the row so the rule is a rule
				     rather than a stub. -->
				<div
					class="${this.classMap({
						'min-w-0': true,
						'mx-auto w-full max-w-[calc(min(34%,11rem)+0.75rem+var(--funnel-plot))] mt-3 border-t border-outline-variant pt-3':
							this.hasLossBreakdown && !beside,
						'ml-6 flex-1 border-l border-outline-variant pl-6': beside,
					})}"
				>
					<slot
						name="loss-breakdown"
						@slotchange=${(e: Event) => {
							this.hasLossBreakdown = (e.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0
						}}
					></slot>
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-funnel': SchmancyFunnel
	}
}
